import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Cookie } from "lucide-react";
import { useLang } from "../context/LangContext";
import { api } from "../lib/api";

const KEY = "nc_consent";

const Toggle = ({ on, onChange, disabled, testId }) => (
  <button type="button" disabled={disabled} onClick={() => onChange(!on)} data-testid={testId} aria-pressed={on}
    className={`relative w-9 h-5 rounded-full transition-colors duration-200 shrink-0 ${on ? "bg-[#5698C7]" : "bg-slate-300"} ${disabled ? "opacity-60 cursor-not-allowed" : ""}`}>
    <span className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform duration-200 ${on ? "translate-x-4" : ""}`} />
  </button>
);

export default function CookieBanner({ reopen = 0 }) {
  const { t, lang } = useLang();
  const [visible, setVisible] = useState(() => !localStorage.getItem(KEY));
  const [prefs, setPrefs] = useState(false);
  const [analytics, setAnalytics] = useState(false);
  const [marketing, setMarketing] = useState(false);

  useEffect(() => {
    if (reopen > 0) {
      const saved = JSON.parse(localStorage.getItem(KEY) || "{}");
      setAnalytics(!!saved.analytics);
      setMarketing(!!saved.marketing);
      setPrefs(true);
      setVisible(true);
    }
  }, [reopen]);

  const save = (a, m, action) => {
    const saved = JSON.parse(localStorage.getItem(KEY) || "{}");
    const consentId = saved.consent_id || crypto.randomUUID();
    localStorage.setItem(KEY, JSON.stringify({ consent_id: consentId, necessary: true, analytics: a, marketing: m, action, ts: new Date().toISOString() }));
    api.post("/public/consent", { consent_id: consentId, analytics: a, marketing: m, action, lang }).catch(() => {});
    setVisible(false);
    setPrefs(false);
  };

  if (!visible) return null;
  return (
    <div className="fixed bottom-0 inset-x-0 z-[70] p-3 sm:p-4" data-testid="cookie-banner">
      <div className="max-w-4xl mx-auto bg-white border border-slate-200 rounded-xl shadow-[0_8px_30px_rgba(15,23,42,0.15)] p-5 sm:p-6">
        <div className="flex items-start gap-3">
          <span className="w-8 h-8 rounded-lg bg-[#5698C7]/10 text-[#5698C7] flex items-center justify-center shrink-0"><Cookie size={16} strokeWidth={1.5} /></span>
          <div>
            <div className="font-mono-tech text-xs tracking-[0.2em] font-semibold text-[#0B1220] mb-1">{t("cookie_title").toUpperCase()}</div>
            <p className="text-xs text-slate-500 leading-relaxed">
              {t("cookie_text")}{" "}
              <Link to="/legal/politica-de-cookies" className="text-[#5698C7] underline hover:text-[#0B1220] transition-colors duration-200" data-testid="cookie-policy-link">{t("cookie_policy")}</Link>.
            </p>
          </div>
        </div>

        {prefs && (
          <div className="mt-4 border-t border-slate-100 pt-4 space-y-3" data-testid="cookie-prefs-panel">
            {[{ key: "necessary", on: true, disabled: true, set: null, testId: "cookie-necessary-toggle" },
              { key: "analytics", on: analytics, disabled: false, set: setAnalytics, testId: "cookie-analytics-toggle" },
              { key: "marketing", on: marketing, disabled: false, set: setMarketing, testId: "cookie-marketing-toggle" }].map((c) => (
              <div key={c.key} className="flex items-center justify-between gap-4">
                <div>
                  <div className="text-xs font-semibold text-[#0B1220]">{t(`cookie_${c.key}`)}{c.disabled && <span className="ml-2 font-mono-tech text-[9px] tracking-widest text-emerald-600">{t("cookie_always_on").toUpperCase()}</span>}</div>
                  <p className="text-[11px] text-slate-400">{t(`cookie_${c.key}_desc`)}</p>
                </div>
                <Toggle on={c.on} onChange={c.set || (() => {})} disabled={c.disabled} testId={c.testId} />
              </div>
            ))}
          </div>
        )}

        <div className="mt-4 flex flex-wrap items-center gap-2">
          <button onClick={() => save(true, true, "accept_all")} data-testid="cookie-accept-all-btn"
            className="bg-[#5698C7] text-white text-xs font-mono-tech tracking-widest px-4 py-2 hover:bg-[#0B1220] transition-colors duration-200">
            {t("cookie_accept_all").toUpperCase()}
          </button>
          <button onClick={() => save(false, false, "reject_all")} data-testid="cookie-reject-btn"
            className="border border-slate-300 text-slate-600 text-xs font-mono-tech tracking-widest px-4 py-2 hover:border-[#0B1220] hover:text-[#0B1220] transition-colors duration-200">
            {t("cookie_reject").toUpperCase()}
          </button>
          {prefs ? (
            <button onClick={() => save(analytics, marketing, "custom")} data-testid="cookie-save-btn"
              className="border border-[#5698C7] text-[#5698C7] text-xs font-mono-tech tracking-widest px-4 py-2 hover:bg-[#5698C7] hover:text-white transition-colors duration-200">
              {t("cookie_save").toUpperCase()}
            </button>
          ) : (
            <button onClick={() => setPrefs(true)} data-testid="cookie-configure-btn"
              className="text-slate-500 text-xs font-mono-tech tracking-widest px-2 py-2 hover:text-[#5698C7] transition-colors duration-200">
              {t("cookie_configure").toUpperCase()}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
